import { computed, observable, runInAction } from 'mobx'
import { BehaviorSubject, type Observable } from 'rxjs'
import { distinctUntilChanged, take } from 'rxjs/operators'
import { NotFound } from 'fejl'
import { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil, shareLatest, whileActive } from '@/utils/rx-util'
import { type MemberStore } from '@/features/iam/members/MemberStore'
import { Member } from '@/features/iam/members/Member'
import { User } from '@/features/iam/users/User'
import { type UserStore } from '@/features/iam/users/UserStore'
import { type UserDataStore } from '@/features/iam/users/UserDataStore'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { type WorkspaceMembershipStore } from '@/features/iam/workspaces/WorkspaceMembershipStore'
import type { Membership } from '@/features/iam/workspaces/Membership'
import { type InvitationStore } from '@/features/iam/invitations/InvitationStore'
import { type IamApi } from '@/features/iam/api/iam-api'
import { type SessionInfo } from '@/features/iam/auth/AuthStore'

/**
 * Tracks session information such as the current user and workspace.
 */
export class SessionStore {
  /**
   * The authenticated user.
   */
  @observable private accessor _currentUser: User | null = null

  /**
   * Current workspace.
   */
  @observable private accessor _currentWorkspace: Workspace | null = null

  /**
   * Whether the session's first workspace preselection has finished,
   * regardless of a workspace having been selected.
   */
  private readonly _workspacePreselectionCompleted$ = new BehaviorSubject(false)

  /**
   * Rx Observable for when the user is logged in. Subscribing after being logged in will
   * emit the current user immediately.
   */
  readonly currentUser$: Observable<User> = toStream(
    () => this._currentUser,
    true,
  ).pipe(
    filterNonNil(),
    shareLatest(),
    take(1), // Only fire it once per subscriber.
  )

  /**
   * Rx Observable for when a workspace has been selected. Emits the current user's workspace member in that workspace.
   * Subscribing after the fact will emit the current member immediately.
   */
  readonly currentMember$: Observable<Member> = toStream(
    () => this._member,
    true,
  ).pipe(filterNonNil(), shareLatest(), distinctUntilChanged())

  /**
   * Emits the current workspace, including `null` if none is selected.
   * Emission starts after the first workspace preselection has completed.
   */
  readonly currentWorkspace$: Observable<Workspace | null> = toStream(
    () => this._currentWorkspace,
    true,
  ).pipe(whileActive(this._workspacePreselectionCompleted$), shareLatest())

  constructor(
    private readonly _api: Pick<IamApi, 'authentication'>,
    private readonly _userStore: Pick<UserStore, 'fetchUserUsingToken'>,
    private readonly _workspaceStore: Pick<
      WorkspaceStore,
      'fetchWorkspace' | 'fetchWorkspaceByIdOrSlug'
    >,
    private readonly _memberStore: Pick<
      MemberStore,
      'fetchMember' | 'getMember'
    >,
    private readonly _workspaceMembershipStore: Pick<
      WorkspaceMembershipStore,
      'listWorkspaceMemberships' | 'addMembership'
    >,
    private readonly _invitationStore: Pick<
      InvitationStore,
      'fetchInvitation' | 'accept'
    >,
    private readonly _userDataStore: Pick<UserDataStore, 'fetchByPublicId'>,
  ) {}

  /**
   * Determines whether you are authenticated.
   */
  @computed
  get isAuthenticated() {
    return !!this._currentUser
  }

  /**
   * Gets the currently authenticated user.
   */
  @computed
  get currentUser() {
    return this._currentUser ?? User.none
  }

  /**
   * Gets the currently authenticated users connection name by
   * parsing it from the id
   */
  @computed
  get enterpriseConnection() {
    const parts = this.currentUser.id?.split('|') ?? []
    return parts.length === 3 ? parts[1] : null
  }

  /**
   * Gets the currently selected workspace.
   */
  @computed
  get currentWorkspace() {
    return this._currentWorkspace ?? Workspace.none
  }

  /**
   * Whether a workspace is selected.
   */
  @computed
  get hasWorkspaceSelected() {
    return !!this._currentWorkspace
  }

  /**
   * Used for getting the current workspace member without throwing.
   */
  @computed
  private get _member() {
    const user = this._currentUser
    const workspace = this._currentWorkspace
    if (!user || !workspace) {
      return null
    }
    return this._memberStore.getMember(workspace.id, user.id)
  }

  /**
   * The current workspace member.
   */
  @computed
  get currentMember() {
    return this._member ?? Member.none
  }

  /**
   * Determines if user is using an external identity provider to log out
   */
  @computed
  get isFederatedLogout() {
    return Boolean(
      this.currentWorkspace.config.signup.federatedLogout &&
      this.currentUser.authId?.toLocaleLowerCase().startsWith('samlp|'),
    )
  }

  /**
   * Gets the current member. If a Workspace ID is specified, returns the member for that workspace.
   *
   * @param workspaceId
   */
  getCurrentMember(workspaceId?: Id) {
    if (!workspaceId) {
      return this.currentMember
    }
    const found = this._memberStore.getMember(workspaceId, this.currentUser.id)
    if (!found) {
      throw new Error(
        'There is no workspace member record for the currently logged-in user and requested workspace.',
      )
    }
    return found
  }

  /**
   * Authenticates the session's user with the given session.
   */
  async authenticate(session: SessionInfo, signal?: AbortSignal) {
    const authUser = await this._api.authentication.authenticate(
      session.token,
      signal,
    )
    const user = await this._userStore.fetchUserUsingToken(
      authUser.public_id,
      session.token,
      signal,
    )

    runInAction(() => {
      if (session.mfaEnabled) {
        user.markMultiFactorAuthEnabled()
      }
      user.markFullyLoaded()
      this._currentUser = user
    })
  }

  /**
   * Loads the authenticated user's memberships and data, accepting the
   * invitation first when there is a token.
   */
  async fetchMemberships(inviteToken?: string): Promise<LoadedMemberships> {
    const membershipsPromise =
      this._workspaceMembershipStore.listWorkspaceMemberships()
    const invitedWorkspace = inviteToken
      ? await this._acceptInvitation(inviteToken)
      : null
    const memberships = await membershipsPromise
    await this._userDataStore.fetchByPublicId(this.currentUser.publicId)
    return { invitedWorkspace, memberships }
  }

  /**
   * Marks the session's first workspace preselection as done, which lets
   * {@link currentWorkspace$} emit.
   */
  completeWorkspacePreselection() {
    this._workspacePreselectionCompleted$.next(true)
  }

  /**
   * Selects the specified workspace.
   */
  async selectWorkspace(workspace: Workspace, signal?: AbortSignal) {
    const id = Id.assert(workspace.id)
    const [fetched] = await Promise.all([
      this._workspaceStore.fetchWorkspace(id, undefined, signal),
      this._memberStore.fetchMember({
        workspaceId: id,
        userId: this.currentUser.id,
        userPublicId: this.currentUser.publicId,
      }),
    ])
    signal?.throwIfAborted()

    runInAction(() => {
      this._currentWorkspace = fetched
    })
  }

  private async _acceptInvitation(inviteToken: string) {
    const invitation = await this._invitationStore
      .fetchInvitation(inviteToken)
      .catch(NotFound.ignore(null))
    if (invitation?.status !== 'Pending') {
      return null
    }

    const workspace = await this._workspaceStore.fetchWorkspaceByIdOrSlug(
      invitation.workspaceId,
    )
    if (!workspace) {
      return null
    }

    const member = await this._invitationStore.accept(invitation.token)
    this._workspaceMembershipStore.addMembership({ workspace, member })
    return workspace
  }
}

/**
 * Result of {@link SessionStore.fetchMemberships}.
 */
export interface LoadedMemberships {
  /**
   * The workspace of the accepted invitation, if any.
   */
  invitedWorkspace: Workspace | null

  /**
   * The user's workspace memberships.
   */
  memberships: Membership[]
}
